import { useState, useRef, useMemo, useEffect } from 'react';
import { D, st, getToken } from '../constants';
import { MAT_CATALOG_FLAT } from '../data.js';
import { extractPriceSheet } from '../lib/priceSheet.js';

export default function SupplyHousePricing({ matPrices, setMatPrices, matPriceHistory, setMatPriceHistory, apiSaveMatPrices }) {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [vendor, setVendor] = useState('');
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dragging, setDragging] = useState(false);
  // Set only when a PDF was read deterministically. Its presence is what puts
  // the confirm gate in front of the import; the vision fallback has no basis
  // to confirm and goes straight to the review table as before.
  const [sheet, setSheet] = useState(null);
  // Every supplier already in the price history, most recently used first.
  // This is both the default and the dropdown - a contractor buys from one or
  // two places, so the right answer is almost always already here.
  const knownSuppliers = useMemo(() => {
    const seen = new Map();
    for (const arr of Object.values(matPriceHistory || {})) {
      if (!Array.isArray(arr)) continue;
      for (const e of arr) {
        if (!e || !e.supplier) continue;
        const prev = seen.get(e.supplier) || '';
        if (String(e.date || '') >= prev) seen.set(e.supplier, String(e.date || ''));
      }
    }
    return [...seen.entries()].sort((a, b) => b[1].localeCompare(a[1])).map(x => x[0]);
  }, [matPriceHistory]);
  const [confirmed, setConfirmed] = useState(false);
  const fileRef = useRef(null);
  // Prefill once, and only while the box is untouched - never overwrite typing.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || vendor || !knownSuppliers.length) return;
    prefilled.current = true;
    setVendor(knownSuppliers[0]);
  }, [knownSuppliers, vendor]);
  const cameraRef = useRef(null);

  const extractPage = async (canvas) => {
    const b64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
    const prompt = `This is a page from an electrical supply house price list/catalog. Extract every material line item visible. Return ONLY a JSON object (no markdown):
{"items": [{"desc": "material description", "unit": "ea, ft, box, or lot", "price": price per unit as a number}]}
Skip headers, page numbers, and anything that is not a priced material line. If a price is a range or unclear, use your best estimate. If nothing readable, return {"items":[]}.`;
    const resp = await fetch('/api/ai/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + getToken() },
      body: JSON.stringify({
        model: 'claude-haiku-4-5-20251001', max_tokens: 4000,
        context: 'supply_house_pdf_import',
        messages: [{ role: 'user', content: [
          { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: b64 } },
          { type: 'text', text: prompt },
        ] }],
      }),
    });
    const data = await resp.json();
    if (data.error) throw new Error(data.error.message || 'AI error');
    const text = (data.content || []).map(c => c.text || '').join('');
    const parsed = JSON.parse(text.replace(/```json|```/g, '').trim());
    return Array.isArray(parsed.items) ? parsed.items : [];
  };

  const extractImage = async (file) => {
    const b64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = ev => resolve(ev.target.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const prompt = `This is a photo of an electrical supply house receipt or invoice. Extract every material line item with its unit price. Return ONLY a JSON object (no markdown):
{"vendor": "supply house name", "items": [{"desc": "material description", "unit": "ea, ft, box, or lot", "price": price per unit as a number}]}
Use "ft" for wire/conduit sold by length, "ea" for individual items, "box" for boxed quantities. Skip tax lines, totals, and non-material lines. If nothing readable, return {"vendor":"","items":[]}.`;
    const resp = await fetch('/api/ai/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + getToken() },
      body: JSON.stringify({
        model: 'claude-haiku-4-5-20251001', max_tokens: 2000,
        context: 'supply_house_image_import',
        messages: [{ role: 'user', content: [
          { type: 'image', source: { type: 'base64', media_type: file.type || 'image/jpeg', data: b64 } },
          { type: 'text', text: prompt },
        ] }],
      }),
    });
    const data = await resp.json();
    if (data.error) throw new Error(data.error.message || 'AI error');
    const text = (data.content || []).map(c => c.text || '').join('');
    const parsed = JSON.parse(text.replace(/```json|```/g, '').trim());
    if (parsed.vendor && !vendor) setVendor(parsed.vendor);
    return Array.isArray(parsed.items) ? parsed.items : [];
  };

  const handleFile = async (file) => {
    setScanning(true); setError(''); setItems(null); setSaved(false);
    setSheet(null); setConfirmed(false);
    try {
      let allItems = [];
      if (file.type.startsWith('image/')) {
        setProgress('Reading image...');
        allItems = await extractImage(file);
      } else {
        const pdfjsLib = await import('pdfjs-dist');
        const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        // A price list PDF has a real text layer. Read it rather than taking a
        // picture of it: the numbers come back exact, the Per/UOM column comes
        // with them, and no model is involved. Verified against a known-good
        // hand parse of a 40-page CED sheet - 2,484 of 2,484 rows, and the
        // same ft/ea classification on every one.
        setProgress('Reading price sheet...');
        const res = await extractPriceSheet(pdf, setProgress);
        if (res.ok) {
          // A name printed on the sheet beats a remembered default, but match
          // it against suppliers already known first so "CED" does not become a
          // second entry alongside "CED Naples".
          if (res.supplierHint) {
            const hit = knownSuppliers.find(s => res.supplierHint.toLowerCase().includes(s.toLowerCase()));
            if (hit) setVendor(hit);
            else if (!vendor) setVendor(res.supplierHint.split(/\s{2,}|\|/)[0].slice(0, 40).trim());
          }
          setSheet(res);
          setItems(res.items.map(it => ({
            desc: it.desc, unit: it.billUnit, price: it.unitCost, include: true,
            srcPrice: it.srcPrice, srcLabel: it.srcLabel, basisQty: it.basisQty,
            pkgQty: it.pkgQty, pkgLabel: it.pkgLabel, cat: it.cat,
            unitUncertain: it.unitUncertain,
          })));
          setScanning(false); setProgress('');
          return;
        }

        // No usable text layer - fall back to vision, page by page.
        setProgress('No text layer found, reading as images...');
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          setProgress(`Reading page ${pageNum} of ${pdf.numPages}...`);
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 2 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width; canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;
          const pageItems = await extractPage(canvas);
          allItems.push(...pageItems);
        }
      }
      if (allItems.length === 0) {
        setError("Couldn't find any priced items.");
        setScanning(false); setProgress(''); return;
      }
      setItems(allItems.map(it => ({ desc: it.desc || 'Item', unit: it.unit || 'ea', price: Number(it.price) || 0, include: true })));
    } catch (e) {
      setError("Couldn't read file: " + e.message);
    }
    setScanning(false); setProgress('');
  };

  const onFileChange = e => { const file = e.target.files?.[0]; if (file) handleFile(file); e.target.value = ''; };
  const updateItem = (i, patch) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  // Flat map first, then the newest price-history entry, then the built-in
  // catalog. The history fallback is what makes this column work at all for
  // anything imported from a supply-house sheet, which only writes history.
  const priceFor = (desc) => matPrices?.[desc]
    ?? matPriceHistory?.[desc]?.[0]?.price
    ?? (MAT_CATALOG_FLAT.find(m => m.desc === desc)?.cost ?? null);

  const save = async () => {
    if (!items) return;
    const toApply = items.filter(it => it.include && it.desc && it.price > 0);
    if (toApply.length === 0) { setError('Nothing selected to import.'); return; }
    // A price with no supplier is nearly useless: history is keyed by supplier,
    // bidding prefers one supplier over another, and the comparison view groups
    // on it. Defaulting to "Unknown Supplier" quietly manufactures a second
    // supply house holding a copy of every price.
    if (!vendor.trim()) { setError('Enter the supply house name before importing - prices are stored per supplier.'); return; }
    setSaving(true); setError('');
    try {
      const today = new Date().toISOString().slice(0, 10);
      const newMatPrices = { ...matPrices };
      const newMatPriceHistory = { ...matPriceHistory };
      toApply.forEach(it => {
        // Same fallback order as priceFor(). Without the history leg this is 0
        // for anything imported from a sheet, because a sheet import writes
        // history and the flat map starts empty.
        const oldPrice = newMatPrices[it.desc]
          ?? matPriceHistory?.[it.desc]?.[0]?.price
          ?? (MAT_CATALOG_FLAT.find(m => m.desc === it.desc)?.cost || 0);
        newMatPrices[it.desc] = it.price;
        // Keep what the sheet literally printed alongside the canonical unit
        // cost. Storing only the divided number throws away the evidence, and
        // the evidence is what makes a wrong basis findable later.
        const entry = { price: it.price, oldPrice, supplier: vendor.trim() || 'Unknown Supplier', date: today, unit: it.unit || 'ea' };
        if (it.srcLabel) { entry.srcPrice = it.srcPrice; entry.srcLabel = it.srcLabel; entry.basisQty = it.basisQty; }
        if (it.pkgQty) { entry.pkgQty = it.pkgQty; entry.pkgLabel = it.pkgLabel; }
        if (it.cat) entry.cat = it.cat;
        newMatPriceHistory[it.desc] = [entry, ...(newMatPriceHistory[it.desc] || [])].slice(0, 10);
      });
      await apiSaveMatPrices(newMatPrices, newMatPriceHistory);
      setMatPrices(newMatPrices);
      setMatPriceHistory(newMatPriceHistory);
      setSaved(true); setItems(null);
    } catch (e) { setError('Failed to save: ' + e.message); }
    setSaving(false);
  };

  const inp = { ...st.input, width: '100%', boxSizing: 'border-box' };
  const includedCount = items ? items.filter(it => it.include).length : 0;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <div
          onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onClick={() => !scanning && fileRef.current?.click()}
          style={{
            flex: 1, border: '2px dashed ' + (dragging ? D.accent : D.border),
            borderRadius: 10, padding: '24px 16px', textAlign: 'center',
            cursor: scanning ? 'default' : 'pointer',
            background: dragging ? D.accent + '11' : 'transparent',
            transition: 'border-color 0.15s, background 0.15s',
          }}
        >
          {scanning
            ? <div style={{ color: D.muted, fontSize: 13 }}>{progress || 'Reading...'}</div>
            : <><div style={{ fontSize: 26, marginBottom: 4 }}>📄</div><div style={{ color: D.text, fontSize: 13, fontWeight: 500 }}>Upload PDF or Image</div><div style={{ color: D.muted, fontSize: 12, marginTop: 4 }}>or drag & drop</div></>
          }
        </div>
        <div
          onClick={() => !scanning && cameraRef.current?.click()}
          style={{
            width: 110, flexShrink: 0, border: '2px dashed ' + D.border,
            borderRadius: 10, padding: '24px 12px', textAlign: 'center',
            cursor: scanning ? 'default' : 'pointer',
          }}
        >
          <div style={{ fontSize: 26, marginBottom: 4 }}>📷</div>
          <div style={{ color: D.text, fontSize: 13, fontWeight: 500 }}>Take Photo</div>
          <div style={{ color: D.muted, fontSize: 12, marginTop: 4 }}>camera</div>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={onFileChange} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={onFileChange} />
      {error && <div style={{ color: D.danger, fontSize: 13, marginTop: 10 }}>{error}</div>}
      {saved && <div style={{ color: D.success, fontSize: 13, marginTop: 10 }}>Prices updated.</div>}
      {sheet && !confirmed && (
        <div style={{ marginTop: 18, border: '1px solid ' + D.accent + '66', background: D.accent + '0d', borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: D.text, marginBottom: 2 }}>Check how this sheet was read</div>
          <div style={{ fontSize: 12, color: D.muted, marginBottom: 12 }}>
            Supply houses quote some items per 100 and others per 1,000. Reading that column wrong makes prices 100x or 1,000x out,
            so confirm these look right before anything is saved.
          </div>
          <div style={{ fontSize: 12, color: D.text, marginBottom: 10 }}>
            <b>{sheet.items.length}</b> items found.{' '}
            {sheet.nonEach > 0
              ? <><b>{sheet.nonEach}</b> of them are priced per 100 or per 1,000 — those are the ones worth checking.</>
              : <>Every item is priced individually.</>}
          </div>
          <div style={{ background: D.surface, borderRadius: 8, padding: 10, marginBottom: 12, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
            {[...sheet.items.filter(r => r.basisQty > 1 && r.billUnit === 'ft').slice(0, 2),
              ...sheet.items.filter(r => r.basisQty > 1 && r.billUnit === 'ea').slice(0, 2),
              ...sheet.items.filter(r => r.basisQty === 1).slice(0, 1)].map((r, i) => (
              <div key={i} style={{ padding: '3px 0', color: D.text }}>
                <span style={{ color: D.success }}>${r.unitCost.toFixed(4)}/{r.billUnit}</span>
                <span style={{ color: D.muted }}>{'  \u2190  '}${r.srcPrice.toFixed(2)} per {r.srcLabel}</span>
                <span style={{ color: D.muted }}>{'   '}{r.desc.slice(0, 34)}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: D.muted, marginBottom: 12 }}>
            Read from the sheet's own columns — price was {sheet.roles.price?.pct}% currency, the unit column {sheet.roles.unit?.pct}% unit codes.
            {sheet.skipped?.length > 0 && <> {sheet.skipped.length} row{sheet.skipped.length === 1 ? '' : 's'} had no price and {sheet.skipped.length === 1 ? 'was' : 'were'} skipped.</>}
          </div>
          <button style={{ ...st.btn('primary'), marginLeft: 0 }} onClick={() => setConfirmed(true)}>These look right</button>
          <button style={{ ...st.btn() }} onClick={() => { setSheet(null); setItems(null); }}>Cancel</button>
        </div>
      )}
      {items && (!sheet || confirmed) && (
        <div style={{ marginTop: 18 }}>
          <div style={{ marginBottom: 12, maxWidth: 280 }}>
            <div style={{ fontSize: 12, color: D.muted, marginBottom: 4 }}>Supply House Name <span style={{ color: D.warning }}>*</span></div>
            <input style={inp} list="supply-house-names" placeholder="e.g. City Electric Supply" value={vendor} onChange={e => setVendor(e.target.value)} />
            {knownSuppliers.length > 0 && <datalist id="supply-house-names">{knownSuppliers.map(s => <option key={s} value={s} />)}</datalist>}
            {knownSuppliers.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {knownSuppliers.slice(0, 4).map(s => (
                  <button key={s} onClick={() => setVendor(s)}
                    style={{ ...st.btn(), marginLeft: 0, padding: '3px 9px', fontSize: 12,
                             borderColor: vendor === s ? D.accent : D.border,
                             color: vendor === s ? D.accent : D.muted }}>{s}</button>
                ))}
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: D.muted, marginBottom: 10 }}>
            Found {items.length} item{items.length === 1 ? '' : 's'}. Review and uncheck any you don't want to import.
          </div>
          <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto', border: `1px solid ${D.border}`, borderRadius: 8 }}>
            <table style={st.table}>
              <thead><tr>{['', 'Description', 'Unit', 'New Price', 'Current Price', 'On the sheet'].map(h => <th key={h} style={st.th}>{h}</th>)}</tr></thead>
              <tbody>
                {items.map((it, i) => {
                  const current = priceFor(it.desc);
                  return (
                    <tr key={i}>
                      <td style={st.td}><input type="checkbox" checked={it.include} onChange={e => updateItem(i, { include: e.target.checked })} /></td>
                      <td style={st.td}><input style={{ ...st.input, minWidth: 160 }} value={it.desc} onChange={e => updateItem(i, { desc: e.target.value })} /></td>
                      <td style={st.td}><input style={{ ...st.input, width: 60 }} value={it.unit} onChange={e => updateItem(i, { unit: e.target.value })} /></td>
                      <td style={st.td}><input style={{ ...st.input, width: 80 }} type="number" step="0.01" value={it.price} onChange={e => updateItem(i, { price: Number(e.target.value) || 0 })} /></td>
                      <td style={{ ...st.td, color: D.muted, fontSize: 12 }}>{current != null ? '$' + Number(current).toFixed(2) : '—'}</td>
                      <td style={{ ...st.td, color: D.muted, fontSize: 11 }}>
                        {it.srcLabel ? '$' + Number(it.srcPrice).toFixed(2) + ' per ' + it.srcLabel : '—'}
                        {it.pkgLabel && <div>{it.pkgLabel} = ${(it.price * it.pkgQty).toFixed(2)}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!vendor.trim() && <div style={{ fontSize: 12, color: D.warning, marginTop: 12 }}>Enter the supply house name above — prices are stored per supplier, and the comparison view needs it.</div>}
          <button style={{ ...st.btn('primary'), marginTop: 14, marginLeft: 0 }} onClick={save} disabled={saving || includedCount === 0 || !vendor.trim()}>
            {saving ? 'Saving...' : 'Import ' + includedCount + ' Price' + (includedCount === 1 ? '' : 's')}
          </button>
        </div>
      )}
    </div>
  );
}
